import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Dialog,
	TextField,
	Typography,
	FormControl,
	Autocomplete,
} from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IStudyAssignModalProps,
	IStudyAssignFormData,
} from 'src/features/study_assignment/domain/types';
import { CreateStudyAssignmentValidationSchema } from 'src/features/study_assignment/domain/validation';
import { STUDY_ASSIGNMENT_LOADING_KEY } from 'src/features/study_assignment/store';
import { createStudyAssignment } from 'src/features/study_assignment/store/studyAssignmentThunks';
import { AppDispatch, RootState } from 'src/store';

export const StudyAssignmentModal: React.FC<IStudyAssignModalProps> = ({
	open,
	siteUsers,
	studyList,
	onClose,
	onSubmit,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const { siteSlug } = useParams<{ siteSlug: string }>();

	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[STUDY_ASSIGNMENT_LOADING_KEY],
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<IStudyAssignFormData>({
		resolver: yupResolver(CreateStudyAssignmentValidationSchema),
		defaultValues: {
			site_access_ids: [],
		},
	});

	const modalTitle =
		studyList.length > 1
			? `Assign ${studyList.length} Studies`
			: 'Assign Study';

	const handleClose = () => {
		if (isLoading) return;
		onClose();
	};

	const handleCreateStudyAssignment = async (data: IStudyAssignFormData) => {
		if (!siteSlug || studyList.length === 0) return;

		let successCount = 0;
		let errorOccurred = false;

		const assignmentPromises = data.site_access_ids.flatMap((userId) =>
			studyList.map(
				(study) =>
					new Promise<void>((resolve) => {
						dispatch(
							createStudyAssignment({
								request: {
									site_access_id: userId,
									study_id: Number(study.id),
								},
								siteSlug,
								onSuccess: () => {
									successCount++;
									resolve();
								},
								onError: () => {
									errorOccurred = true;
									resolve();
								},
							}),
						);
					}),
			),
		);

		await Promise.all(assignmentPromises);

		onSubmit?.();

		const studiesToAssign = studyList.length;

		if (errorOccurred) {
			snackbar(
				`Successfully assigned stud${studiesToAssign === 1 ? 'y' : 'ies'}. But, few assignments failed.`,
				'warning',
			);
		} else if (successCount > 1) {
			snackbar(
				`${studiesToAssign} studies assigned successfully`,
				'success',
			);
		} else if (successCount === 1) {
			snackbar('Study assigned successfully', 'success');
		} else {
			snackbar('Assignment failed for all selected studies.', 'error');
		}
	};

	useEffect(() => {
		if (open) {
			reset();
		}
	}, [open, reset]);

	if (!open) return null;

	return (
		<Dialog open={open} onClose={handleClose} fullWidth>
			<form onSubmit={handleSubmit(handleCreateStudyAssignment)}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						padding: '24px',
						gap: 3,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
						}}
					>
						<Typography
							id='modal-modal-title'
							variant='h6'
							fontWeight='bold'
						>
							{modalTitle}
						</Typography>
						<CloseIcon
							onClick={handleClose}
							sx={{
								cursor: isLoading ? 'not-allowed' : 'pointer',
								color: isLoading ? 'gray' : 'black',
							}}
						/>
					</Box>
					<Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 3,
							}}
						>
							<>
								<Controller
									name='site_access_ids'
									control={control}
									render={({
										field: { onChange, value, ...field },
									}) => (
										<FormControl
											fullWidth
											error={!!errors?.site_access_ids}
										>
											<Autocomplete
												{...field}
												multiple
												limitTags={2}
												options={siteUsers}
												getOptionLabel={(option) =>
													`${option.first_name} ${option.last_name} - ${option.email}`
												}
												value={
													siteUsers.filter((user) =>
														value?.includes(
															user.id,
														),
													) || null
												}
												onChange={(_, newValues) => {
													onChange(
														newValues.map(
															(user) => user.id,
														),
													);
												}}
												ListboxProps={{
													style: {
														maxHeight: '300px',
													},
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														label='Users'
														error={
															!!errors?.site_access_ids
														}
														helperText={
															errors
																?.site_access_ids
																?.message
														}
													/>
												)}
											/>
										</FormControl>
									)}
								/>
							</>
						</Box>
					</Box>
					<PrimaryButton
						type='submit'
						isLoading={isLoading}
						sx={{
							width: '100%',
							marginTop: '8px',
						}}
					>
						Assign
					</PrimaryButton>
				</Box>
			</form>
		</Dialog>
	);
};
